import { type FormatOption, FormatOptions, Scale, ScaleStep } from "./NumberModel";

export const formatBigNumber = (intValue: number, formatOption: FormatOption): string => {
    if (intValue % formatOption.scaleStep === 0) {
        return `${intValue / formatOption.scaleStep}${formatOption.suffix}`;
    }

    const floatNumber = intValue / formatOption.scaleStep;
    const parsed = Number.parseInt(floatNumber.toFixed(0), 10);

    if (parsed === ScaleStep) {
        return `${(parsed / ScaleStep).toFixed(0)}${formatOption.nextSuffix}`;
    }

    return `${floatNumber.toFixed(formatOption.fractionDigits)}${formatOption.suffix}`;
};

export const formatNumbers = (value: number): string => {
    if (Number.isNaN(value)) {
        return "-";
    }

    if (0 <= value && value < Scale.k) {
        return `${value}`;
    }

    if (value < Scale.k * 100) {
        return (value / Scale.k).toFixed(3).replace(".", ",");
    }

    if (Scale.k * 100 <= value && value < Scale.m) {
        return formatBigNumber(value, FormatOptions[Scale.k]);
    }

    if (Scale.m <= value && value < Scale.b) {
        return formatBigNumber(value, FormatOptions[Scale.m]);
    }

    return formatBigNumber(value, FormatOptions[Scale.b]);
};

export const formatNumberNoSuffix = (value: number): string => {
    if (Number.isNaN(value)) {
        return value.toString();
    }

    if (0 <= value && value < Scale.k) {
        return `${value}`;
    }

    return value.toString().replaceAll(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export const formatSignedPctNumber = (value: string): string => {
    if (!value || value === "N/A") {
        return value;
    }

    const [integer, decimal] = value.split(".");
    const intValue = Number.parseInt(integer ?? "", 10);
    if (Number.isNaN(intValue)) {
        return value;
    }

    const sign = Number.parseFloat(value) < 0 ? "-" : "+";
    const decimalString = Number.parseFloat(value) >= Scale.k * 100 ? "" : `.${decimal}`;
    return `${sign}${formatNumbers(Math.abs(intValue))}${decimalString}%`;
};

export const formatWithUSFormat = (value: number): string => {
    if (Number.isNaN(value)) {
        return "N/A";
    }
    const formatter = new Intl.NumberFormat("en-US");
    return formatter.format(value);
};

export const formatNullableNumber = (value: number | null | undefined): string =>
    value === null || value === undefined ? "-" : formatWithUSFormat(value);
